import { GroupFields } from 'src/components/People/Groups/types';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { SHORT_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { sdk } from 'src/utilities/sdk';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';

type UseAddGroupProps = {
  onSuccess: (groupId: string) => void;
};

type UseAddGroupReturn = {
  mutate: (groupInfo: GroupFields, corpId: string) => Promise<void>;
};

export const useAddGroup = ({
  onSuccess,
}: UseAddGroupProps): UseAddGroupReturn => {
  const { sendAnalytics } = useSendAnalytics({
    eventcategory: 'self-service management tool_people',
    eventaction: 'create group_cta',
  });

  async function mutate(groupInfo: GroupFields, corpId: string): Promise<void> {
    try {
      const response = await sdk.http.post(
        `/corp-admin/${corpId}/groups`,
        groupInfo
      );

      const groupId = response.id;

      // use a promise to allow form 'isSubmitting' to handle button disabled state
      await new Promise<void>((resolve) => {
        // add search delay in hopes of ensuring new data reaches the elastic search in time
        setTimeout(() => {
          pushSuccess('This group has been added.');

          onSuccess(groupId);
          resolve();
          sendAnalytics({
            eventlabel: 'success',
          });
        }, SHORT_SEARCH_DELAY);
      });
    } catch ({ status, data }) {
      const fallbackErrorMessage =
        'There was a problem adding this group. Try again.';

      pushErrorFromAPI(
        {
          status,
          errors: data,
        },
        {
          fallbackMessage: fallbackErrorMessage,
        }
      );

      sendAnalytics({
        eventlabel: 'error',
      });
    }
  }

  return {
    mutate,
  };
};
